import { useEffect, useState } from 'react';
import { ChevronLeft, ChevronRight, ExternalLink, FileImage, FileText, Paperclip } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { FormattedClinicalText } from '@/components/FormattedClinicalText';
import { ImagingAttachmentModal } from '@/components/ImagingAttachmentModal';
import { deMarkdownSnippet } from '@/lib/note-filters';
import { extractNoteText } from '@/utils/html-to-text';
import { openAttachmentInNewTab, type ImagingAttachment } from '@/utils/open-attachment';
import type { ReplitDocument, ReplitImagingStudy } from '@/types/replit-dashboard';
import ExplainButton from './ExplainButton';
import RewriteView from './RewriteView';
import { useDocumentRewrite } from './useDocumentRewrite';
import { formatRecordDateTime } from './records-utils';

interface ImagingSectionProps {
  studies: ReplitImagingStudy[];
  /** DocumentReference imaging reports; listed with the studies rather than under Notes. */
  reportDocuments: ReplitDocument[];
  onAskQuestion: (question: string, displayText?: string) => void;
  variant: 'main' | 'rail';
}

/** One row: a DiagnosticReport/ImagingStudy or an imaging report document, normalized. */
interface ImagingItem {
  id: string;
  name: string;
  date: string;
  /** Report text to show and rewrite; empty when the study only carries attachments. */
  text: string;
  attachments: ImagingAttachment[];
  subtitle?: string;
}

/** Server placeholders that stand in for a missing description. */
const DESCRIPTION_PLACEHOLDERS = ['no description available', 'see attached report for details'];
const EXPLAIN_TEXT_CHARS = 2000;

const attachmentText = (attachment: ImagingAttachment): string => {
  if (attachment.type !== 'text' && attachment.type !== 'html') return '';
  const text = extractNoteText({ type: attachment.type, data: attachment.data, contentType: attachment.contentType });
  return /^\[.*\]$/.test(text.trim()) || text === 'No content available' ? '' : text;
};

const studyText = (study: ReplitImagingStudy): string => {
  const description = (study.description || '').trim();
  const cleaned = DESCRIPTION_PLACEHOLDERS.includes(description.toLowerCase()) ? '' : description;
  const fromAttachments = (study.attachments || []).map((a) => attachmentText(a as ImagingAttachment)).reduce((best, t) => (t.length > best.length ? t : best), '');
  return fromAttachments.length > cleaned.length ? fromAttachments : cleaned;
};

const timeOf = (value: string) => {
  const t = new Date(value).getTime();
  return Number.isNaN(t) ? 0 : t;
};

const attachmentIcon = (type: string) => {
  if (type === 'pdf') return <FileText className="h-4 w-4 text-red-600" />;
  if (type === 'image') return <FileImage className="h-4 w-4 text-blue-600" />;
  return <Paperclip className="h-4 w-4 text-gray-600" />;
};

interface ImagingDetailProps {
  item: ImagingItem;
  onBack: () => void;
  onExplain: () => void;
  onAskQuestion: ImagingSectionProps['onAskQuestion'];
}

function ImagingDetail({ item, onBack, onExplain, onAskQuestion }: ImagingDetailProps) {
  const { t, i18n } = useTranslation();
  const [openAttachment, setOpenAttachment] = useState<ImagingAttachment | null>(null);
  const { rewrite, isFetching, isError, fetch } = useDocumentRewrite('imaging', item.id, item.name, item.text);
  const hasText = item.text.length > 0;
  useEffect(() => { if (hasText) fetch(); }, [fetch, hasText]);

  return (
    <div data-testid="imaging-detail">
      <button onClick={onBack} className="inline-flex items-center gap-1 text-sm text-text-secondary hover:text-text-primary mb-3" data-testid="button-imaging-back">
        <ChevronLeft className="h-4 w-4" />
        {t('records.backToAllImaging')}
      </button>
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="min-w-0">
          <h3 className="text-base font-semibold text-text-primary">{item.name}</h3>
          <p className="text-sm text-text-secondary mt-0.5">
            {item.subtitle ? `${item.subtitle} · ` : ''}{formatRecordDateTime(item.date, i18n.resolvedLanguage || 'en')}
          </p>
        </div>
        <ExplainButton onClick={onExplain} ariaLabel={t('explain.imagingAriaLabel', { type: item.name })} testId={`button-explain-imaging-${item.id}`} />
      </div>
      {hasText ? (
        <RewriteView
          rewrite={rewrite}
          isFetching={isFetching}
          isError={isError}
          originalLabel={t('records.tabRadiologistReport')}
          plainSubline={t('records.plainImagingSubline')}
          originalSubline={t('records.originalImagingSubline')}
          original={<FormattedClinicalText text={item.text} />}
        />
      ) : (
        <p className="text-sm text-text-secondary">{t('records.imagingNoReportText')}</p>
      )}
      {item.attachments.length > 0 && (
        <div className="border-t pt-3 mt-3">
          <h5 className="text-xs font-semibold text-gray-700 mb-2">{t('records.attachments')}</h5>
          <div className="flex flex-wrap gap-2">
            {item.attachments.map((attachment, index) => (
              <Button
                key={index}
                size="sm"
                variant="outline"
                onClick={() => {
                  if (attachment.type === 'text' || attachment.type === 'html') setOpenAttachment(attachment);
                  else openAttachmentInNewTab(attachment, t('records.imagingReportFallback'));
                }}
                className="h-8 px-3 flex items-center space-x-2 hover:bg-blue-50 hover:border-blue-300"
              >
                {attachmentIcon(attachment.type)}
                <span className="text-xs">{attachment.title || t('records.viewAttachmentType', { type: attachment.type.toUpperCase() })}</span>
                {attachment.type !== 'text' && attachment.type !== 'html' && <ExternalLink className="h-3 w-3 ml-1" />}
              </Button>
            ))}
          </div>
        </div>
      )}
      <ImagingAttachmentModal
        attachment={openAttachment}
        studyType={item.name}
        studyDate={item.date}
        open={openAttachment !== null}
        onClose={() => setOpenAttachment(null)}
        onAskQuestion={onAskQuestion}
      />
    </div>
  );
}

export default function ImagingSection({ studies, reportDocuments, onAskQuestion, variant }: ImagingSectionProps) {
  const { t, i18n } = useTranslation();
  const language = i18n.resolvedLanguage || 'en';
  const [openId, setOpenId] = useState<string | null>(null);
  const rowPadding = variant === 'rail' ? 'px-3' : 'px-4';

  const items: ImagingItem[] = [
    ...(studies || []).map((study, index) => ({
      id: study.id || `study-${index}`,
      name: study.type || t('records.imagingStudyFallback'),
      date: study.date,
      text: studyText(study),
      attachments: (study.attachments || []) as ImagingAttachment[],
      subtitle: study.performer && study.performer !== 'Unknown' ? study.performer : undefined,
    })),
    ...(reportDocuments || []).map((doc) => ({
      id: String(doc.id),
      name: doc.noteType || t('records.imagingReportFallback'),
      date: doc.createdAt,
      text: doc.content || '',
      attachments: [],
      subtitle: doc.authorName || undefined,
    })),
  ].sort((a, b) => timeOf(b.date) - timeOf(a.date));

  if (items.length === 0) {
    return (
      <Card className="p-6">
        <p className="text-gray-500 text-sm">{t('records.noImagingStudiesAvailable')}</p>
      </Card>
    );
  }

  const explain = (item: ImagingItem) => {
    let question = t('explain.imagingBase', { type: item.name });
    if (item.text) question += t('explain.imagingFindings', { description: item.text.slice(0, EXPLAIN_TEXT_CHARS) });
    onAskQuestion(question);
  };

  const openItem = openId !== null ? items.find((item) => item.id === openId) : undefined;
  if (openItem) {
    return <ImagingDetail item={openItem} onBack={() => setOpenId(null)} onExplain={() => explain(openItem)} onAskQuestion={onAskQuestion} />;
  }

  return (
    <div className="rounded-lg border bg-white divide-y">
      {items.map((item) => (
        <div key={item.id} className={`flex items-start justify-between gap-3 ${rowPadding} py-2.5 hover:bg-gray-50`} data-testid={`row-imaging-${item.id}`}>
          <button onClick={() => setOpenId(item.id)} className="text-left flex-1 min-w-0" data-testid={`button-open-imaging-${item.id}`}>
            <span className="block text-sm font-medium text-text-primary">{item.name}</span>
            <span className="block text-sm text-text-secondary mt-0.5">{formatRecordDateTime(item.date, language)}</span>
            {item.text && <span className="block text-sm text-text-secondary mt-1 line-clamp-1">{deMarkdownSnippet(item.text, 1)}</span>}
          </button>
          <span className="flex items-center gap-2 flex-shrink-0 mt-0.5">
            <ExplainButton onClick={() => explain(item)} ariaLabel={t('explain.imagingAriaLabel', { type: item.name })} testId={`button-explain-imaging-${item.id}`} />
            <ChevronRight className="h-4 w-4 text-text-secondary" />
          </span>
        </div>
      ))}
    </div>
  );
}
